import { makeFarmClient } from "@/lib/farm-client";
import { queryClient } from "@/lib/query-client";
import { DSAprBreakdown } from "@myth-finance/dualstake-farm-sdk";
import { AprBreakdown } from "@myth-finance/dualstake-farm-sdk/dist/DualstakeFarmClient";
import {
  DSContractListing,
  DSContractState,
} from "@myth-finance/dualstake-ts-sdk";
import { useQuery } from "@tanstack/react-query";

export interface FullAprBreakdown extends DSAprBreakdown {
  netBaseAprBps: bigint;
  netCurrentBlockBonus: bigint;
}

export function postProcessAprBreakdown(
  cState: DSContractState | DSContractListing,
  aprBreakdown: AprBreakdown
): FullAprBreakdown {
  const totalFeeBps =
    "platformFeeBps" in cState
      ? cState.platformFeeBps + cState.noderunnerFeeBps
      : 700n;
  const aprRecalc = 1 - Number(totalFeeBps) / 10000;
  return {
    ...aprBreakdown,
    netBaseAprBps: BigInt(
      Math.floor(aprRecalc * Number(aprBreakdown?.baseAprBps))
    ),
    netCurrentBlockBonus: BigInt(
      Math.floor(aprRecalc * Number(aprBreakdown?.currentBlockBonus))
    ),
  };
}

export const useAprBreakdown = (
  cState: DSContractState,
  overrideFarmAmount?: bigint
) => {
  return useQuery({
    queryKey: [
      "apr",
      String(cState.appId),
      overrideFarmAmount ? String(overrideFarmAmount) : "9000000",
    ],
    staleTime: 60 * 1_000,
    queryFn: async (): Promise<FullAprBreakdown | null> => {
      if (!cState?.appId) return null;
      console.debug("Requesting APR", cState.appId, overrideFarmAmount);
      const aprBreakdown = await makeFarmClient().getProjectedAPR({
        dsAppId: cState.appId,
        overrideFarmAmount: overrideFarmAmount ?? 9_000_000n,
      });

      return postProcessAprBreakdown(cState, aprBreakdown);
    },
  });
};

export const clearAPRCache = (appId?: bigint) => {
  const queryKey = ["apr"];
  if (appId) queryKey.push(String(appId));
  queryClient.invalidateQueries({ queryKey });
};
